import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

const shapes = {
  rod_center: { name: "Thin Rod (center)", formula: "(1/12)ML²" },
  rod_end: { name: "Thin Rod (end)", formula: "(1/3)ML²" },
  disk: { name: "Solid Disk/Cylinder", formula: "(1/2)MR²" },
  ring: { name: "Thin Ring/Hoop", formula: "MR²" },
  sphere_solid: { name: "Solid Sphere", formula: "(2/5)MR²" },
  sphere_hollow: { name: "Hollow Sphere", formula: "(2/3)MR²" },
  rectangle: { name: "Rectangle (center)", formula: "(1/12)M(a²+b²)" },
};

export default function MomentInertiaCalculator() {
  const [shape, setShape] = useState("disk");
  const [mass, setMass] = useState("");
  const [dimension1, setDimension1] = useState("");
  const [dimension2, setDimension2] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateInertia = () => {
    const m = parseFloat(mass);
    const d1 = parseFloat(dimension1);
    const d2 = dimension2 ? parseFloat(dimension2) : 0;

    if (isNaN(m) || isNaN(d1)) {
      toast.error("Please enter valid values");
      return;
    }

    let inertia = 0;
    switch (shape) {
      case "rod_center":
        inertia = (1/12) * m * d1 * d1;
        break;
      case "rod_end":
        inertia = (1/3) * m * d1 * d1;
        break;
      case "disk":
        inertia = 0.5 * m * d1 * d1;
        break;
      case "ring":
        inertia = m * d1 * d1;
        break;
      case "sphere_solid":
        inertia = 0.4 * m * d1 * d1;
        break;
      case "sphere_hollow":
        inertia = (2/3) * m * d1 * d1;
        break;
      case "rectangle":
        if (isNaN(d2)) {
          toast.error("Please enter both dimensions");
          return;
        }
        inertia = (1/12) * m * (d1 * d1 + d2 * d2);
        break;
    }

    setResult(inertia);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setMass("");
    setDimension1("");
    setDimension2("");
    setResult(null);
  };

  const getDimensionLabel = () => {
    if (shape.includes("rod")) return "Length (m)";
    if (shape === "rectangle") return "Width a (m)";
    return "Radius (m)";
  };

  const needsSecondDimension = shape === "rectangle";

  return (
    <PageLayout calculatorName="Moment of Inertia Calculator" calculatorPath="/calculator/moment-inertia">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Moment of Inertia Calculator</h1>
          <p className="text-muted-foreground">
            Calculate rotational inertia for various shapes
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Moment of Inertia: I = mr²</CardTitle>
            <CardDescription>
              Resistance to rotational acceleration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Shape</Label>
                <Select value={shape} onValueChange={setShape}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(shapes).map(([key, data]) => (
                      <SelectItem key={key} value={key}>
                        {data.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Formula: I = {shapes[shape as keyof typeof shapes].formula}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mass">Mass (kg)</Label>
                <Input
                  id="mass"
                  type="number"
                  step="0.01"
                  placeholder="Enter mass"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dimension1">{getDimensionLabel()}</Label>
                <Input
                  id="dimension1"
                  type="number"
                  step="0.01"
                  placeholder={`Enter ${getDimensionLabel().toLowerCase()}`}
                  value={dimension1}
                  onChange={(e) => setDimension1(e.target.value)}
                />
              </div>

              {needsSecondDimension && (
                <div className="space-y-2">
                  <Label htmlFor="dimension2">Height b (m)</Label>
                  <Input
                    id="dimension2"
                    type="number"
                    step="0.01"
                    placeholder="Enter height"
                    value={dimension2}
                    onChange={(e) => setDimension2(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateInertia} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(4)} kg·m²
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Moment of Inertia
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["moment-inertia"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Common Shapes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Disk/Cylinder: I = ½MR²</p>
                <p>• Solid Sphere: I = ⅖MR²</p>
                <p>• Thin Rod (center): I = 1/12 ML²</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 2 kg disk with 0.5 m radius = 0.25 kg·m²
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
